import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Briefcase, Loader2, RefreshCw, AlertTriangle, Check, Plus, Copy, Trash2, Save,
  ChevronUp, ChevronDown,
} from 'lucide-react';
import { apiService, ConfigInspection, ConfigSectionsQuery, ContainerApplyResult } from '@/services/api.service';
import { useGameStore } from '@/stores/game.store';
import { loadPackPalette, PalettePickEntry } from '@/components/config/packPalette';
import { SearchPicker, PickerOption } from '@/components/config/SearchPicker';
import { JobsXpTab } from '@/components/config/JobsXpTab';
import { ApplyReport, ContainerScope, NoReference, isAbsentEverywhere } from '@/components/config/AllContainers';
import styles from './LootboxPage.module.scss';

// ============================================================
// Récompenses de métier de JobsMod (JobRewards), communes à tous les containers du projet
// ============================================================
// JobsMod seulement : RecubeJobsMod (NationsGlory) lit un fichier du MÊME nom au même endroit
// (data/mods/jobs.config.json), qui a sa propre page (RecubeJobsPage). La requête nomme donc le mod :
// le serveur ne retient que les containers dont le release-version.json porte JobsMod, et le fichier
// que JobsMod lit (directement dans le dossier des mods). Avant, la recherche par nom attrapait aussi
// le fichier de RecubeJobsMod et la page l'éditait comme celui de JobsMod.
//
// Structure : JobRewards[<metier>][<niveau>] = [ { Type, ItemId, Amount, NameItem?, UsageId? } ].
// Types observés : Money (ItemId = la monnaie), Item, UnlockUsage et UnlockCraft (déblocages, qui
// portent en plus un libellé et un UsageId).
//
// Pas de choix de container : la page lit jobs.config.json dans le container de référence (le premier,
// par id, qui porte le fichier) et l'enregistrement écrit JobRewards dans CHAQUE container du projet
// qui le porte. Le bandeau dit lesquels en diffèrent aujourd'hui.
//
// ATTENTION : ce fichier contient aussi DatabaseSettings (chaîne de connexion Mongo) et
// ProfileApiSettings. Le serveur ne renvoie que JobRewards et n'écrit que JobRewards : ces sections
// ne quittent jamais le container et restent propres à chacun.
// ============================================================

/** Le seul fichier de config Jobs édité par cette page : celui que JobsMod lit. */
const JOBS_CONFIG = 'jobs.config.json';

/** Fichier, mod et section gérés par cette page, dans chaque container du projet. */
const JOBS_QUERY: ConfigSectionsQuery = {
  fileNames: [JOBS_CONFIG],
  keys: ['JobRewards'],
  mod: 'JobsMod',
};

const REWARD_TYPES = ['Money', 'Item', 'UnlockUsage', 'UnlockCraft'] as const;
type RewardType = (typeof REWARD_TYPES)[number];

const TYPE_LABEL: Record<RewardType, string> = {
  Money: 'Argent',
  Item: 'Item',
  UnlockUsage: 'Débloque utilisation',
  UnlockCraft: 'Débloque craft',
};

interface Reward {
  Type?: string;
  ItemId?: string;
  Amount?: number;
  NameItem?: string;
  UsageId?: string;
  [k: string]: unknown;
}

/** Ce que la page édite : la seule section JobRewards (le serveur ne renvoie rien d'autre). */
interface JobsSections {
  JobRewards: Record<string, Record<string, Reward[]>>;
}

function isUnlock(t: string | undefined): boolean {
  return t === 'UnlockUsage' || t === 'UnlockCraft';
}

export function JobsPage() {
  const activeGameId = useGameStore((s) => s.activeGameId);
  const [inspection, setInspection] = useState<ConfigInspection | null>(null);
  const [doc, setDoc] = useState<JobsSections | null>(null);
  const [orig, setOrig] = useState('');
  const [job, setJob] = useState('');
  const [level, setLevel] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<{ ok: boolean; msg: string } | null>(null);
  const [report, setReport] = useState<ContainerApplyResult | null>(null);
  const [palette, setPalette] = useState<PalettePickEntry[]>([]);
  const [tab, setTab] = useState<'rewards' | 'xp'>('rewards');
  /**
   * jobs.config.json absent partout : les autres fichiers porteurs de JobRewards, container par container,
   * pour ne pas chercher à l'aveugle (affichés seulement, jamais ouverts ni écrits).
   */
  const [otherConfigs, setOtherConfigs] = useState<Array<{ containerName: string; paths: string[] }>>([]);
  /** Dernière lecture lancée : une réponse plus ancienne (projet précédent) est ignorée. */
  const loadSeq = useRef(0);

  // Même liste que les catalogues Blocks / Items du projet, avec les vignettes.
  useEffect(() => {
    loadPackPalette(activeGameId).then(setPalette).catch(() => setPalette([]));
  }, [activeGameId]);

  const itemOptions = useMemo<PickerOption[]>(
    () => palette.map((p) => ({
      value: p.id,
      label: p.id,
      hint: p.kind === 'block' ? 'block' : 'item',
      thumbnailPath: p.path,
      groupId: p.groupId,
    })),
    [palette],
  );

  /**
   * Lit JobRewards dans le container de référence et l'alignement des autres. Le fichier est CHERCHÉ
   * côté serveur dans chaque container dont l'image porte JobsMod, là où le mod le lit (dossier des
   * mods). `keepEdit` ne rafraîchit que le bandeau, après un
   * enregistrement, sans toucher au document en cours d'édition.
   */
  const load = useCallback(async (keepEdit = false) => {
    const seq = ++loadSeq.current;
    setLoading(true);
    if (!keepEdit) {
      setStatus(null);
      setReport(null);
      setDoc(null);
      setOtherConfigs([]);
    }
    const r = await apiService.inspectContainerConfig(JOBS_QUERY);
    if (seq !== loadSeq.current) return;
    if (!keepEdit && r.data && !r.data.referenceContainerId && isAbsentEverywhere(r.data.entries)) {
      // Aucun jobs.config.json de JobsMod : un JobRewards porté par un autre fichier d'un container
      // qui a JobsMod se voit au moins ici (jamais celui d'un container qui ne l'a pas).
      const found = await Promise.all(r.data.entries
        .filter((e) => e.status === 'SKIPPED_FILE_MISSING')
        .map(async (e) => {
          const alt = await apiService.searchContainerFiles(e.containerId, {
            contains: '"JobRewards"', jsonKey: 'JobRewards', jsonKind: 'object', max: 10,
          });
          return { containerName: e.containerName, paths: (alt.data?.entries ?? []).map((f) => f.path) };
        }));
      if (seq !== loadSeq.current) return;
      setOtherConfigs(found.filter((f) => f.paths.length > 0));
    }
    setLoading(false);
    setInspection(r.data ?? null);
    if (keepEdit) return;
    if (!r.data) { setStatus({ ok: false, msg: r.error ?? 'Lecture impossible.' }); return; }
    if (!r.data.referenceContainerId) return; // aucun container ne porte le fichier : dit plus bas
    const rewards = r.data.sections?.JobRewards;
    if (!rewards || typeof rewards !== 'object' || Array.isArray(rewards)) {
      setStatus({ ok: false, msg: `${r.data.referencePath ?? JOBS_CONFIG} ne contient pas de JobRewards.` });
      return;
    }
    const next: JobsSections = { JobRewards: rewards as JobsSections['JobRewards'] };
    setDoc(next);
    setOrig(JSON.stringify(next));
    const firstJob = Object.keys(next.JobRewards)[0] ?? '';
    setJob(firstJob);
    setLevel(Object.keys(next.JobRewards[firstJob] ?? {}).sort((a, b) => Number(a) - Number(b))[0] ?? '');
  }, []);

  useEffect(() => { void load(); }, [activeGameId, load]);

  const jobs = doc?.JobRewards ? Object.keys(doc.JobRewards) : [];
  const levels = useMemo(
    () => Object.keys(doc?.JobRewards?.[job] ?? {}).sort((a, b) => Number(a) - Number(b)),
    [doc, job],
  );
  const rewards: Reward[] = doc?.JobRewards?.[job]?.[level] ?? [];
  /** Autres sections du fichier : préservées dans chaque container, jamais envoyées ici. */
  const otherKeys = inspection?.otherKeys ?? [];

  const setRewards = (next: Reward[]) => {
    setDoc((prev) => {
      if (!prev?.JobRewards) return prev;
      return {
        ...prev,
        JobRewards: {
          ...prev.JobRewards,
          [job]: { ...prev.JobRewards[job], [level]: next },
        },
      };
    });
  };

  const mutate = (i: number, patch: (r: Reward) => Reward) => {
    const next = [...rewards];
    next[i] = patch({ ...next[i] });
    setRewards(next);
  };

  const setType = (i: number, t: RewardType) => {
    mutate(i, (r) => {
      const out: Reward = { ...r, Type: t };
      if (isUnlock(t)) {
        out.UsageId = out.UsageId ?? out.ItemId ?? '';
        out.NameItem = out.NameItem ?? '';
        out.Amount = out.Amount ?? 1;
      } else {
        delete out.NameItem;
        delete out.UsageId;
      }
      return out;
    });
  };

  const addRow = () => setRewards([...rewards, { Type: 'Item', ItemId: '', Amount: 1 }]);
  const duplicate = (i: number) => {
    const next = [...rewards];
    next.splice(i + 1, 0, JSON.parse(JSON.stringify(next[i])) as Reward);
    setRewards(next);
  };
  const remove = (i: number) => setRewards(rewards.filter((_, k) => k !== i));
  const move = (i: number, d: number) => {
    const j = i + d;
    if (j < 0 || j >= rewards.length) return;
    const next = [...rewards];
    [next[i], next[j]] = [next[j], next[i]];
    setRewards(next);
  };

  const addLevel = () => {
    const next = String((levels.length ? Math.max(...levels.map(Number)) : 0) + 1);
    setDoc((prev) => {
      if (!prev?.JobRewards) return prev;
      return {
        ...prev,
        JobRewards: { ...prev.JobRewards, [job]: { ...prev.JobRewards[job], [next]: [] } },
      };
    });
    setLevel(next);
  };

  const serialized = doc ? JSON.stringify(doc) : '';
  const dirty = !!doc && serialized !== orig;

  /** Écrit JobRewards dans chaque container du projet qui porte JobsMod et son fichier, puis dit qui a reçu quoi. */
  const save = async () => {
    if (!doc) return;
    setSaving(true);
    setStatus(null);
    setReport(null);
    const res = await apiService.applyContainerConfig({ ...JOBS_QUERY, sections: { JobRewards: doc.JobRewards } });
    setSaving(false);
    if (!res.data) {
      setStatus({ ok: false, msg: res.error ?? 'Enregistrement en échec' });
      return;
    }
    setReport(res.data);
    // Un container en échec garde la page « non enregistrée » : un nouvel essai réécrit partout.
    if (res.data.success) setOrig(serialized);
    void load(true);
  };

  /** Relit jobs.config.json ; ne jette pas des modifications sans le demander. */
  const reload = () => {
    if (dirty && !window.confirm('Recharger jobs.config.json abandonnera les modifications non enregistrées. Continuer ?')) return;
    void load();
  };

  /** Lecture faite, mais aucun container du projet ne porte le fichier : le pourquoi, container par container. */
  const missing = inspection && !inspection.referenceContainerId ? inspection : null;

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>Jobs</h1>
          <p className={styles.subtitle}>
            {tab === 'rewards'
              ? "Récompenses de palier par métier de JobsMod (argent, items, déblocages de craft et d'utilisation), communes à tous les containers du projet dont l'image porte JobsMod. Le reste du fichier, dont la configuration base de données, n'est ni affiché ni modifié."
              : "Gain d'XP par action : ces objectifs vivent dans le BEHAVIOR PACK Jobs, pas dans la config d'un serveur. Publie le pack depuis Déploiement pour les appliquer."}
          </p>
        </div>
        {tab === 'rewards' && (
          <div className={styles.headerActions}>
            <button className={styles.secondaryBtn} onClick={reload} disabled={loading}>
              {loading ? <Loader2 size={14} className={styles.spin} /> : <RefreshCw size={14} />} Rafraîchir
            </button>
            <button className={styles.primaryBtn} onClick={save} disabled={!dirty || saving}
              title="Écrit les récompenses dans tous les containers du projet qui portent JobsMod et jobs.config.json">
              {saving ? <Loader2 size={14} className={styles.spin} /> : <Save size={14} />} Enregistrer
            </button>
          </div>
        )}
      </header>

      <nav className={styles.tabBar}>
        <button type="button" className={`${styles.tabBtn} ${tab === 'rewards' ? styles.tabBtnOn : ''}`}
          onClick={() => setTab('rewards')}>Récompenses</button>
        <button type="button" className={`${styles.tabBtn} ${tab === 'xp' ? styles.tabBtnOn : ''}`}
          onClick={() => setTab('xp')}>Gain d'XP</button>
      </nav>

      {tab === 'xp' ? <JobsXpTab project={activeGameId} /> : (
      <>

      {inspection && <ContainerScope entries={inspection.entries} referencePath={inspection.referencePath} />}

      {status && (
        <div className={status.ok ? styles.ok : styles.error}>
          {status.ok ? <Check size={14} /> : <AlertTriangle size={14} />} {status.msg}
        </div>
      )}

      {report && <ApplyReport result={report} onClose={() => setReport(null)} />}

        <section className={styles.detail}>
          {!doc ? (
            <div className={styles.detailEmpty}>
              {loading ? (
                <><Loader2 size={22} className={styles.spin} /><p>Ouverture de jobs.config.json…</p></>
              ) : missing && missing.entries.length === 0 ? (
                <>
                  <AlertTriangle size={22} />
                  <p>{missing.message}</p>
                  <p className={styles.muted}>Déclare les containers du projet dans Paramètres généraux → Containers.</p>
                </>
              ) : missing ? (
                <>
                  <NoReference file={JOBS_CONFIG} entries={missing.entries} mod="JobsMod" />
                  <p className={styles.muted}>
                    RecubeJobsMod lit un fichier du même nom : il s'édite dans{' '}
                    <Link to="/config/recubejobs">Configuration → RecubeJobs</Link>.
                  </p>
                  {otherConfigs.length > 0 && (
                    <p className={styles.muted}>
                      Autres fichiers porteurs de JobRewards :{' '}
                      {otherConfigs.map((o) => `${o.containerName} (${o.paths.join(', ')})`).join(' ; ')}.
                    </p>
                  )}
                </>
              ) : (
                <>
                  <Briefcase size={26} />
                  {/* Une ouverture en échec a déjà posé son message au-dessus : ne pas le contredire. */}
                  <p>jobs.config.json n'a pas pu être ouvert.</p>
                </>
              )}
            </div>
          ) : (
            <>
              <div className={styles.boxHead}>
                <label className={styles.field}>
                  <span className={styles.label}>Métier</span>
                  <select className={styles.input} value={job}
                    onChange={(e) => {
                      const j = e.target.value;
                      setJob(j);
                      const ls = Object.keys(doc.JobRewards?.[j] ?? {}).sort((a, b) => Number(a) - Number(b));
                      setLevel(ls[0] ?? '');
                    }}>
                    {jobs.map((j) => <option key={j} value={j}>{j}</option>)}
                  </select>
                </label>
                <label className={styles.field}>
                  <span className={styles.label}>Palier</span>
                  <select className={styles.input} value={level} onChange={(e) => setLevel(e.target.value)}>
                    {levels.map((l) => <option key={l} value={l}>Niveau {l}</option>)}
                  </select>
                </label>
                <div className={styles.totals}>
                  <span><strong>{levels.length}</strong> palier(s)</span>
                  <span><strong>{rewards.length}</strong> récompense(s) sur ce palier</span>
                  <button className={styles.secondaryBtn} onClick={addLevel}><Plus size={13} /> Palier</button>
                </div>
              </div>

              {/* Le fichier lu est nommé dans le bandeau du haut. */}
              {otherKeys.length > 0 && (
                <p className={styles.muted}>
                  Sections propres à chaque container, ni affichées ni modifiées : {otherKeys.join(', ')}.
                </p>
              )}

              <div className={styles.tableHeadJobs}>
                <span>Type</span>
                <span>Item</span>
                <span>Quantité</span>
                <span>Libellé (déblocage)</span>
                <span>UsageId</span>
                <span />
              </div>

              {rewards.length === 0 && <p className={styles.muted}>Aucune récompense sur ce palier.</p>}

              {rewards.map((r, i) => {
                const type = (REWARD_TYPES.includes(r.Type as RewardType) ? r.Type : 'Item') as RewardType;
                const unlock = isUnlock(type);
                return (
                  <div key={i} className={styles.rowEdit}>
                    <div className={styles.cellsJobs}>
                      <span>
                        <select className={styles.input} value={type} onChange={(e) => setType(i, e.target.value as RewardType)}>
                          {REWARD_TYPES.map((t) => <option key={t} value={t}>{TYPE_LABEL[t]}</option>)}
                        </select>
                      </span>
                      <span className={styles.picker}>
                        <SearchPicker
                          groupId={palette[0]?.groupId ?? ''}
                          value={r.ItemId ?? ''}
                          options={itemOptions}
                          showThumbnails
                          placeholder={type === 'Money' ? 'minecraft:gold_nugget' : 'paladium:…'}
                          onChange={(v) => mutate(i, (x) => {
                            const out = { ...x, ItemId: v };
                            // Sur un déblocage, l'UsageId suit l'item tant qu'il n'a pas été personnalisé.
                            if (isUnlock(x.Type) && (!x.UsageId || x.UsageId === x.ItemId)) out.UsageId = v;
                            return out;
                          })}
                        />
                      </span>
                      <span>
                        <input className={styles.input} type="number" min={0} value={r.Amount ?? 1}
                          onChange={(e) => mutate(i, (x) => ({ ...x, Amount: Number(e.target.value) }))} />
                      </span>
                      <span>
                        <input className={styles.input} value={r.NameItem ?? ''} disabled={!unlock}
                          placeholder={unlock ? 'Utilisation de la pioche en améthyste' : '—'}
                          onChange={(e) => mutate(i, (x) => ({ ...x, NameItem: e.target.value }))} />
                      </span>
                      <span>
                        <input className={styles.input} value={r.UsageId ?? ''} disabled={!unlock}
                          placeholder={unlock ? 'paladium:amethyst_pickaxe' : '—'}
                          onChange={(e) => mutate(i, (x) => ({ ...x, UsageId: e.target.value }))} />
                      </span>
                      <span className={styles.rowActions}>
                        <button className={styles.iconBtn} title="Monter" onClick={() => move(i, -1)}><ChevronUp size={13} /></button>
                        <button className={styles.iconBtn} title="Descendre" onClick={() => move(i, 1)}><ChevronDown size={13} /></button>
                        <button className={styles.iconBtn} title="Dupliquer" onClick={() => duplicate(i)}><Copy size={13} /></button>
                        <button className={`${styles.iconBtn} ${styles.iconBtnDanger}`} title="Supprimer" onClick={() => remove(i)}><Trash2 size={13} /></button>
                      </span>
                    </div>
                  </div>
                );
              })}

              <div className={styles.footerActions}>
                <button className={styles.secondaryBtn} onClick={addRow} disabled={!level}>
                  <Plus size={14} /> Ajouter une récompense
                </button>
                <span className={styles.muted}>{dirty ? 'Modifications non enregistrées.' : 'Enregistré.'}</span>
              </div>
            </>
          )}
        </section>
      </>
      )}
    </div>
  );
}
